import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useMessages } from '../../hooks/useMessages';
import MessageInput from './MessageInput';
import Matches from './Matches';
import { Box, Typography, Paper, CircularProgress, Container, Button, Dialog, IconButton } from '@mui/material';
import { collection, query, where, or, and, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import ProfileCard from '../Profile/ProfileCard';
import { doc, getDoc } from 'firebase/firestore';
import { User } from '../../types';
import CloseIcon from '@mui/icons-material/Close';

interface MessageListProps {
  selectedMatchId: string | null;
}

const MessageList: React.FC<MessageListProps> = ({ selectedMatchId }) => {
  const { user } = useAuth();
  const { messages, loading, sendMessage } = useMessages(user?.uid || '');
  const [matchRequest, setMatchRequest] = useState<any>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<User | null>(null);

  useEffect(() => {
    if (!user || !selectedMatchId) return;

    // Fetch match request
    const senderQuery = query(
      collection(db, 'matchRequests'),
      where('status', '==', 'accepted'),
      where('senderId', '==', user.uid),
      where('receiverId', '==', selectedMatchId)
    );

    const receiverQuery = query(
      collection(db, 'matchRequests'),
      where('status', '==', 'accepted'),
      where('senderId', '==', selectedMatchId),
      where('receiverId', '==', user.uid)
    );

    const unsubscribeSender = onSnapshot(senderQuery, (snapshot) => {
      if (!snapshot.empty) {
        setMatchRequest(snapshot.docs[0].data());
      }
    });

    const unsubscribeReceiver = onSnapshot(receiverQuery, (snapshot) => {
      if (!snapshot.empty) {
        setMatchRequest(snapshot.docs[0].data());
      }
    });

    return () => {
      unsubscribeSender();
      unsubscribeReceiver();
    };
  }, [user, selectedMatchId]);

  useEffect(() => {
    if (!selectedMatchId) return;

    const fetchProfile = async () => {
      const userRef = doc(db, 'users', selectedMatchId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSelectedProfile({
          uid: userDoc.id,
          email: data.email || '',
          location: data.location || '',
          ethnicityPreference: data.ethnicityPreference || '',
          displayName: data.displayName || '',
          age: data.age || '',
          gender: data.gender || '',
          bio: data.bio || '',
          interests: data.interests || '',
          occupation: data.occupation || '',
          education: data.education || '',
          lookingFor: data.lookingFor || '',
          profilePicture: data.profilePicture || '',
          additionalPhotos: data.additionalPhotos || [],
          verificationSelfie: data.verificationSelfie || '',
          matches: data.matches || [],
          verified: data.verified || false,
          verificationStatus: data.verificationStatus || 'none',
          height: data.height || '',
          genderPreference: data.genderPreference || '',
          ethnicities: data.ethnicities || [],
          nationality: data.nationality || [],
          emailVerified: data.emailVerified || false,
          birthdate: data.birthdate || ''
        });
      }
    };

    fetchProfile();
  }, [selectedMatchId]);

  if (!user) return null;
  if (!selectedMatchId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="black" variant="h5">
          Select a match to start messaging
        </Typography>
      </Box>
    );
  }

  if (loading) return (
    <Box display="flex" justifyContent="center" p={4}>
      <CircularProgress />
    </Box>
  );

  const filteredMessages = messages.filter(msg => 
    (msg.sender === user.uid && msg.recipient === selectedMatchId) ||
    (msg.recipient === user.uid && msg.sender === selectedMatchId)
  );

  const allMessages = [
    // Add match request message if it exists
    ...(matchRequest ? [{
      id: 'match-request',
      sender: matchRequest.senderId,
      message: matchRequest.message,
      timestamp: matchRequest.timestamp,
      isMatchRequest: true
    }] : []),
    // Add regular messages
    ...filteredMessages
  ].sort((a, b) => 
    (a.timestamp?.toMillis() || 0) - (b.timestamp?.toMillis() || 0)
  );

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      p: 2,
      pb: 0,
      mb: 3
    }}>
      {selectedMatchId && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {selectedProfile?.displayName}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setIsProfileOpen(true)}
          >
            View Profile
          </Button>
        </Box>
      )}

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 2,
        maxHeight: 'calc(100% - 150px)'
      }}>
        {allMessages.map((msg) => (
          <Paper
            key={msg.id}
            sx={{
              p: 2,
              maxWidth: '70%',
              alignSelf: msg.sender === user.uid ? 'flex-end' : 'flex-start',
              bgcolor: msg.isMatchRequest ? 'secondary.light' : 
                      msg.sender === user.uid ? 'primary.light' : 'grey.100',
              color: (msg.isMatchRequest || msg.sender === user.uid) ? 'white' : 'inherit'
            }}
          >
            {msg.isMatchRequest && (
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                Match Request
              </Typography>
            )}
            <Typography variant="body1">{msg.message}</Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
              {msg.timestamp?.toDate().toLocaleString()}
            </Typography>
          </Paper>
        ))}
      </Box>
      
      <Box sx={{ 
        mt: 'auto',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 1
      }}>
        <MessageInput 
          onSendMessage={(message) => {
            if (user && selectedMatchId) {
              sendMessage(user.uid, message, selectedMatchId)
                .catch(error => console.error('Error sending message:', error));
            }
          }} 
        />
      </Box>

      <Dialog 
        open={isProfileOpen} 
        onClose={() => setIsProfileOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        {selectedProfile && (
          <Box sx={{ overflow: 'auto', position: 'relative' }}>
            <IconButton
              onClick={() => setIsProfileOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                },
                zIndex: 1
              }}
            >
              <CloseIcon />
            </IconButton>
            <ProfileCard
              profile={selectedProfile}
              onMatchRequest={() => {}}
              isMatchRequestPage={true}
            />
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default MessageList;